/**
 * Every path in the app, once.
 *
 * Screens link to each other by importing `ROUTES`, never by writing a string.
 * A route that moves then moves everywhere, and a typo is a type error.
 */
import type { Role } from '@botguide/shared';

export const ROUTES = {
  signIn: '/signin',
  /** The delivery board. Owner and contractor. */
  board: '/board',
  /** Owner-only admin over the client > application > project > feature tree. */
  work: '/work',
  people: '/people',
  capacity: '/capacity',
  myHours: '/hours',
  hoursReview: '/hours/review',
  /** The client's own dashboard. */
  dashboard: '/dashboard',
  /** The same dashboard, as an owner previewing one client's view. */
  clientDashboard: (clientId: string) => `/dashboard/${clientId}`,
  forbidden: '/forbidden',
} as const;

/** The route pattern React Router registers for `clientDashboard`. */
export const CLIENT_DASHBOARD_PATTERN = '/dashboard/:clientId';

/**
 * Where a person lands after signing in, and where "/" sends them.
 *
 * Contractors and the owner both start on the board because the board is the
 * work. A client has no board - they get the dashboard, which is the only
 * screen their role can read anything on.
 */
export function landingPathFor(role: Role): string {
  switch (role) {
    case 'owner':
    case 'contractor':
      return ROUTES.board;
    case 'client':
      return ROUTES.dashboard;
  }
}

/**
 * Which roles may open each screen.
 *
 * `App.tsx` guards with this and `pathAfterSignIn` reads it, so a screen's
 * audience is stated once. Written inline in both places, the guard and the
 * redirect drift apart quietly - the screen keeps refusing the role and the
 * redirect keeps sending them to it.
 *
 * The API is still the real boundary. This decides what to render and where to
 * send somebody; it decides nothing about what they may read.
 *
 * `signIn`, `forbidden` and `/` are deliberately absent. They are not screens
 * with an audience, and leaving them out is what makes `pathAfterSignIn` treat
 * a remembered `/signin` as "nothing worth restoring".
 */
export const SCREEN_ROLES: Record<string, readonly Role[]> = {
  [ROUTES.board]: ['owner', 'contractor'],
  // Shared with contractors since 2026-09-22 - the work tree is theirs too.
  [ROUTES.work]: ['owner', 'contractor'],
  [ROUTES.capacity]: ['owner', 'contractor'],
  [ROUTES.people]: ['owner', 'contractor'],
  [ROUTES.myHours]: ['owner', 'contractor'],
  [ROUTES.hoursReview]: ['owner'],
  // The owner gets in so they can preview what a client is shown.
  [ROUTES.dashboard]: ['client', 'owner'],
  [CLIENT_DASHBOARD_PATTERN]: ['client', 'owner'],
};

/**
 * The roles allowed on a concrete path, or undefined if no screen claims it.
 *
 * `CLIENT_DASHBOARD_PATTERN` is the only route with a parameter, so one regex
 * covers the whole parameterised case. If a second parameterised route is ever
 * added, this is the function that has to learn about it.
 */
export function rolesForPath(path: string): readonly Role[] | undefined {
  const withoutQuery = path.split(/[?#]/)[0] ?? '';

  const claimed = SCREEN_ROLES[withoutQuery];
  if (claimed) return claimed;

  const isClientDashboard = /^\/dashboard\/[^/]+$/.test(withoutQuery);
  return isClientDashboard ? SCREEN_ROLES[CLIENT_DASHBOARD_PATTERN] : undefined;
}

/**
 * Where to send somebody who has just signed in.
 *
 * Restoring the page they were on is the right instinct and was wrong often
 * enough to matter. Signing out of a client dashboard and back in as a
 * contractor restored the deep link, the guard refused it, and the first
 * screen of somebody's first day was "Not your screen". `docs/FINDINGS.md`
 * F13.
 *
 * Anything this cannot positively confirm the role may open - an unclaimed
 * path, a mistyped URL, the sign-in page itself - lands them instead. A person
 * who has just proved who they are should see their own work, not a refusal.
 */
export function pathAfterSignIn(remembered: string | undefined, role: Role): string {
  if (!remembered) return landingPathFor(role);
  return rolesForPath(remembered)?.includes(role) ? remembered : landingPathFor(role);
}

export interface NavItem {
  to: string;
  label: string;
  /** Roles that may see the link. The API still decides what they may read. */
  roles: readonly Role[];
  /** Key into the icon set in components/Icon.tsx. */
  icon: 'board' | 'work' | 'people' | 'capacity' | 'hours' | 'review' | 'dashboard';
  /** Rendered under a heading in the sidebar, in this order. */
  group: 'Delivery' | 'Team' | 'Client';
}

export const NAV: readonly NavItem[] = [
  { to: ROUTES.board, label: 'Board', roles: ['owner', 'contractor'], icon: 'board', group: 'Delivery' },
  { to: ROUTES.work, label: 'Work items', roles: ['owner', 'contractor'], icon: 'work', group: 'Delivery' },
  { to: ROUTES.capacity, label: 'Capacity', roles: ['owner', 'contractor'], icon: 'capacity', group: 'Delivery' },
  { to: ROUTES.people, label: 'People', roles: ['owner', 'contractor'], icon: 'people', group: 'Team' },
  { to: ROUTES.myHours, label: 'My hours', roles: ['owner', 'contractor'], icon: 'hours', group: 'Team' },
  { to: ROUTES.hoursReview, label: 'Review hours', roles: ['owner'], icon: 'review', group: 'Team' },
  { to: ROUTES.dashboard, label: 'Dashboard', roles: ['owner', 'client'], icon: 'dashboard', group: 'Client' },
];

export const NAV_GROUPS = ['Delivery', 'Team', 'Client'] as const;
